
import { defineComponent, computed, reactive, watch, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import AppButton from '@/components/atoms/AppButton.vue'
import AppInput from '@/components/atoms/AppInput.vue'
import AppSelect from '@/components/atoms/AppSelect.vue'
import CheckboxList from '../molecules/CheckboxList.vue'
import InputDate from '../molecules/InputDate.vue'
import { eraList } from '../../data/selector/eraList'
import { volumeFederalRegisterItemTypeList } from '@/data/selector/volumeFederalRegisterItemTypeList'
import compareObjectRecursive from '@/helpers/util/compareObjectRecursive'
import { queryConvertToSortKeyAndOrder } from '@/data/SortList'
import convertToArray from '@/helpers/util/convertToArray'
import { federalRegisterTypeList } from '@/data/selector/federalRegisterTypeList'
import { subjectKanpoList } from '@/data/selector/subjectKanpoList'
import ModalSheetMobile from '@/components/organisms/ModalSheetMobile.vue'
import { getSelector } from '@/data/selector/getSelector'
import StringKeyObject from '@/data/@types/StringKeyObject'
import { resetFacet, isIncludeFasetQuery, isFirstScreenTransition } from '@/helpers/util/queryUtils'
import DetailedSearchFooter from '@/components/molecules/DetailedSearchFooter.vue'

/**
 * コレクション詳細画面：官報入力フォーム
 */
export default defineComponent({
  name: 'CollectionDetailedSearchOfficialGazette',
  components: {
    AppButton,
    AppInput,
    AppSelect,
    CheckboxList,
    InputDate,
    ModalSheetMobile,
    DetailedSearchFooter,
  },
  props: {
    collectionId: {
      type: String,
      required: true,
    },
    showMobile: {
      type: Boolean,
      default: false,
    },
  },
  setup (props, context) {
    const i18n = useI18n()
    const lang = i18n.locale
    const volumeFederalRegisterItemTypeListLocale = computed(() => getSelector('volumeFederalRegisterItemTypeList', volumeFederalRegisterItemTypeList, i18n.t))
    const eraListLocale = computed(() => getSelector('eraList', eraList, i18n.t))
    const federalRegisterType = computed(() => getSelector('federalRegisterTypeList', federalRegisterTypeList, i18n.t))
    const subjectKanpo = computed(() => getSelector('subjectKanpoList', subjectKanpoList, i18n.t))
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    const accessRestrictions = [
      { value: 'internet', label: i18n.t('detailSearch.accessRestriction.internet') },
      { value: 'ooc', label: i18n.t('detailSearch.accessRestriction.ooc') },
      { value: 'inlibrary', label: i18n.t('detailSearch.accessRestriction.inlibrary') },
    ]

    const makeFederalRegisterTypeList = (): any[] => {
      // 初回遷移時（route.queryが{"pageNum":"0"}）はデフォルト値を適応
      return route.query.federalRegisterTypeList ? convertToArray(route.query.federalRegisterTypeList) : (isFirstScreenTransition(route.query) ? federalRegisterTypeList.map((federalRegisterTypeMap: StringKeyObject) => federalRegisterTypeMap.value) : [])
    }
    const makeSubjectKanpoList = (): any[] => {
      // 初回遷移時（route.queryが{"pageNum":"0"}）はデフォルト値を適応
      return route.query.subjectKanpoList ? convertToArray(route.query.subjectKanpoList) : (isFirstScreenTransition(route.query) ? subjectKanpoList.map((subjectKanpoMap: StringKeyObject) => subjectKanpoMap.value) : [])
    }
    const initialQuery: StringKeyObject = {
      ...route.query,
      keyword: route.query.keyword,
      subjectKanpoList: makeSubjectKanpoList(),
      volumeSubtitle: route.query.volumeSubtitle,
      partTitleKanpo: route.query.partTitleKanpo,
      publicationPlace: route.query.publicationPlace,
      accessRestrictions: store.getters.getAccessRestrictionsCheckList,
      federalRegisterTypeList: makeFederalRegisterTypeList(),
      eraType: route.query.eraType ? route.query.eraType : 'AD',
      publicationFrom: route.query.publicationFrom ? route.query.publicationFrom : null,
      publicationTo: route.query.publicationTo ? route.query.publicationTo : null,
      volumeFederalRegisterItemType: route.query.volumeFederalRegisterItemType ? route.query.volumeFederalRegisterItemType : '0',
      volumeFederalRegister: route.query.volumeFederalRegister,
      partTitleKanpoNumber: route.query.partTitleKanpoNumber,
      includeVolumeNum: route.query.includeVolumeNum ? route.query.includeVolumeNum : 'true',
    }
    const query: StringKeyObject = reactive(initialQuery)
    const state = reactive({
      showAll: false,
    })

    // 1つ前の検索条件保存
    const queryCache = computed(() => store.getters.cacheQuery)

    /** ローカルのrouteキャッシュ。前回pushした内容を保持する。 */
    let queryLocalCache = {}

    /**
     * 1. 連続検索チェック(検索実行中は検索ボタンを押しても実行させない)
     * 2. 入力フォームを検索条件に変換（whereの作成）
     * 3. whereから不要な項目を削除（未入力内容など）
     * 4. 検索条件の初期化項目を設定
     * 5. route内容を比較して前回と同じだった場合はそのまま検索Action、違う場合はroute更新
     */
    const search = async () => {
      // 検索中に再度検索が呼び出された場合、実行しない
      const isSearching = store.getters.itemIsProcessing
      if (isSearching) return

      const searchMode = computed(() => store.getters.searchMode)
      // 公開範囲：チェック状態保持
      store.commit('SET_ACCESS_RESTRICTIONS', query.accessRestrictions)

      if (query.publicationFrom && query.publicationFrom.toString().length === 10) {
        query.fromYear = query.publicationFrom.toString().substr(0, 4) !== '0000' ? query.publicationFrom.toString().substr(0, 4) : ''
        query.fromMonth = query.publicationFrom.toString().substr(5, 2) !== '00' ? query.publicationFrom.toString().substr(5, 2) : ''
        query.fromDay = query.publicationFrom.toString().substr(8) !== '00' ? query.publicationFrom.toString().substr(8) : ''
      }
      if (query.publicationTo && query.publicationTo.toString().length === 10) {
        query.toYear = query.publicationTo.toString().substr(0, 4) !== '0000' ? query.publicationTo.toString().substr(0, 4) : ''
        query.toMonth = query.publicationTo.toString().substr(5, 2) !== '00' ? query.publicationTo.toString().substr(5, 2) : ''
        query.toDay = query.publicationTo.toString().substr(8) !== '00' ? query.publicationTo.toString().substr(8) : ''
      }
      let request: StringKeyObject = {
        ...query,
        collection: [props.collectionId],
        pageNum: '0',
        fullText: true,
        pageSize: searchMode.value.pageSize || '20',
        sortKey: searchMode.value.sortKey || 'SCORE',
        displayMode: searchMode.value.displayMode || 'list',
      }
      let useQuery: StringKeyObject = {
        ...query,
        collection: [props.collectionId],
        pageNum: '0',
        fullText: true,
        pageSize: searchMode.value.pageSize || '20',
        sortKey: searchMode.value.sortKey || 'SCORE',
        displayMode: searchMode.value.displayMode || 'list',
      }
      for (var propName in useQuery) {
        if (!useQuery[propName]) {
          delete useQuery[propName]
        }
        if (Array.isArray(useQuery[propName]) && !useQuery[propName].length) {
          delete useQuery[propName]
        }
        if (!useQuery[propName] && propName !== 'includeVolumeNum') {
          delete useQuery[propName]
        }
        if (useQuery[propName] && (useQuery[propName].toString() === '0000-00-00' && ['publicationFrom', 'publicationTo'].includes(propName))) {
          delete useQuery[propName]
        }
        if (['fromYear', 'fromMonth', 'fromDay', 'toYear', 'toMonth', 'toDay'].includes(propName)) {
          delete useQuery[propName]
        }
      }
      if (!useQuery.publicationFrom && !useQuery.publicationTo) {
        delete useQuery.eraType
      }
      if (!useQuery.volumeFederalRegister) {
        delete useQuery.volumeFederalRegisterItemType
      }
      delete useQuery.collection
      useQuery = resetFacet(useQuery)
      request = resetFacet(request)

      request.sortKey = route.query.sortKey ? queryConvertToSortKeyAndOrder(route.query.sortKey.toString()).sortKey : 'SCORE'
      request.order = route.query.sortKey ? queryConvertToSortKeyAndOrder(route.query.sortKey.toString()).order : 'DESC'

      if (isFirstScreenTransition(route.query) || isIncludeFasetQuery(route.query)) {
        // 初回遷移時（route.queryが{"pageNum":"0"}の時） or ファセットが選択されている時
        queryLocalCache = useQuery
        router.push({ path: `/collections/${props.collectionId}`, query: { ...useQuery } })
      } else {
        if (compareObjectRecursive(useQuery, queryLocalCache)) {
          await store.dispatch('collectionDetailSearch', request)
        } else {
          if (isFirstScreenTransition(queryLocalCache) && compareObjectRecursive(useQuery, queryCache.value)) {
            // 詳細画面からブラウザバックした時はqueryLocalCacheがリセットされるため
            await store.dispatch('collectionDetailSearch', request)
          } else {
            queryLocalCache = useQuery
            router.push({ path: `/collections/${props.collectionId}`, query: { ...useQuery } })
          }
        }
      }
      // 検索実行したらモーダルを閉じる
      context.emit('close')
    }
    const firstAccessRestriction = store.getters.getAccessRestrictionsCheckList

    const publicationResetKey = ref<number>(0)

    watch(route, () => {
      query.accessRestrictions = route.query.accessRestrictions ? convertToArray(route.query.accessRestrictions) : firstAccessRestriction
      query.keyword = route.query.keyword ? route.query.keyword.toString() : ''
      query.eraType = route.query.eraType ? route.query.eraType.toString() : 'AD'
      query.publicationFrom = route.query.publicationFrom ? route.query.publicationFrom.toString() : ''
      query.publicationTo = route.query.publicationTo ? route.query.publicationTo.toString() : ''
      publicationResetKey.value++
      query.subjectKanpoList = route.query.subjectKanpoList ? convertToArray(route.query.subjectKanpoList) : (isFirstScreenTransition(route.query) ? subjectKanpoList.map((subjectKanpoMap: StringKeyObject) => subjectKanpoMap.value) : []) // ブラウザバック時（route.queryが{"pageNum":"0"}）はデフォルト値を適応
      query.volumeSubtitle = route.query.volumeSubtitle ? route.query.volumeSubtitle.toString() : ''
      query.partTitleKanpo = route.query.partTitleKanpo ? route.query.partTitleKanpo.toString() : ''
      query.partTitleKanpoNumber = route.query.partTitleKanpoNumber ? route.query.partTitleKanpoNumber.toString() : ''
      query.federalRegisterTypeList = route.query.federalRegisterTypeList ? convertToArray(route.query.federalRegisterTypeList) : (isFirstScreenTransition(route.query) ? federalRegisterTypeList.map((federalRegisterTypeMap: StringKeyObject) => federalRegisterTypeMap.value) : []) // ブラウザバック時（route.queryが{"pageNum":"0"}）はデフォルト値を適応
      query.volumeFederalRegisterItemType = route.query.volumeFederalRegisterItemType ? route.query.volumeFederalRegisterItemType.toString() : '0'
      query.volumeFederalRegister = route.query.volumeFederalRegister ? route.query.volumeFederalRegister.toString() : ''
    })

    const clearForm = () => {
      query.keyword = ''
      query.subjectKanpoList = subjectKanpoList.map((subjectKanpoMap: StringKeyObject) => subjectKanpoMap.value)
      query.volumeSubtitle = ''
      query.partTitleKanpo = ''
      query.publicationPlace = ''
      query.eraType = 'AD'
      query.partTitleKanpoNumber = ''
      query.federalRegisterTypeList = federalRegisterTypeList.map((federalRegisterTypeMap: StringKeyObject) => federalRegisterTypeMap.value)
      query.publicationFrom = ''
      query.publicationTo = ''
      publicationResetKey.value++
      query.volumeFederalRegisterItemType = '0'
      query.volumeFederalRegister = ''
    }
    return {
      query,
      state,
      lang,
      volumeFederalRegisterItemTypeListLocale,
      federalRegisterType,
      subjectKanpo,
      eraListLocale,
      accessRestrictions,
      search,
      clearForm,
      publicationResetKey,
    }
  },
})
